import { useEffect } from 'react';
import { LayoutDashboard, Store } from 'lucide-react';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppHeader, AppShell, cn } from '@beatq/ui';
import { AccountMenu, SubjectImpersonationBanner } from '@beatq/shared';
import {
  listBranches,
  branchesFromEnvelope,
} from '../features/branch-settings/branchSettingsApi.js';
import {
  getOrderBoard,
  isActionableIncomingOrder,
  isOperationallyLiveOrder,
  ordersFromEnvelope,
} from '../features/order-board/orderBoardApi.js';
import {
  BRANCH_SETUP_PARAM,
  isOnboardingSetupPath,
} from '../features/onboarding/branchSetupState.js';
import { onboardingChecklistPath } from '../features/onboarding/onboardingState.js';
import { setStoredBranchId } from '../features/branch-settings/branchSettingsState.js';
import { MerchantNavIcon } from './merchantIcons.js';
import { MerchantNotificationsBell } from './MerchantNotificationsBell.js';
import { MerchantStoreSelector } from './MerchantStoreSelector.js';
import { MerchantStoreStatusToggle } from './MerchantStoreStatusToggle.js';
import { currentMerchantScope } from './merchantContext.js';
import {
  filterMerchantNavForRoles,
  getMerchantRoleCodes,
  shouldShowTradingPauseControl,
  resolveMerchantNavSection,
} from './merchantNavAccess.js';
import { merchantHomePathFromBranches } from './merchantPreLive.js';
import { MerchantSectionGate } from './MerchantSectionGate.js';
import {
  merchantOpsPollMs,
  useMerchantOrderRealtime,
} from '../features/order-board/useMerchantOrderRealtime.js';
import { clearStaffLabel, readStaffLabel } from './merchantChrome.js';
import { MerchantOnboardingAuthLayout } from './MerchantOnboardingAuthLayout.js';
import { MerchantSectionLinks } from './MerchantSectionLinks.js';

function navClassName(isActive: boolean) {
  return cn('mer-nav-link', isActive && 'is-active');
}

function navGroup(icon: string) {
  if (['orders', 'catalogue', 'inventory'].includes(icon)) return 'Service';
  if (['promotions', 'finance', 'reviews', 'reports'].includes(icon)) return 'Business';
  return 'Management';
}

export function MerchantShell() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const roles = getMerchantRoleCodes();
  const visibleNav = filterMerchantNavForRoles(roles);
  const isBranchSetup =
    searchParams.get(BRANCH_SETUP_PARAM) === '1' && isOnboardingSetupPath(location.pathname);
  const branchesQuery = useQuery({
    queryKey: ['merchant', 'branches'],
    queryFn: listBranches,
  });
  const branches = branchesFromEnvelope(branchesQuery.data);
  const homeHref = merchantHomePathFromBranches(branches, roles);
  const { branchId } = currentMerchantScope();
  const accountLabel = readStaffLabel();

  const { live: socketsLive } = useMerchantOrderRealtime(true);
  const boardQuery = useQuery({
    queryKey: ['merchant', 'orders', 'board'],
    queryFn: () => getOrderBoard(),
    refetchInterval: merchantOpsPollMs(socketsLive, 15_000),
    enabled: visibleNav.some((item) => item.icon === 'orders'),
  });
  const boardOrders = ordersFromEnvelope(boardQuery.data);
  const incomingCount = boardOrders.filter((order) => isActionableIncomingOrder(order)).length;
  const liveCount = boardOrders.filter((order) => isOperationallyLiveOrder(order)).length;

  useEffect(() => {
    if (branchId) setStoredBranchId(branchId);
  }, [branchId]);

  if (isBranchSetup) {
    return (
      <div className="merchant-console is-branch-setup" data-branch-setup-shell="true">
        <MerchantOnboardingAuthLayout />
      </div>
    );
  }

  return (
    <div
      className={cn('merchant-console w-full max-w-none', isBranchSetup && 'is-branch-setup')}
      data-branch-setup-shell={isBranchSetup ? 'true' : undefined}
    >
      <AppShell
        className="w-full max-w-none"
        banners={<SubjectImpersonationBanner />}
        header={
          <AppHeader
            productName="BeatQ Eats Merchant"
            homeHref={homeHref}
            renderBrand={({ homeHref: href }) => (
              <Link to={href} className="mer-wordmark">
                <span className="mer-wordmark-beatq">BeatQ Eats</span>
                <span className="mer-wordmark-merchant">Merchant</span>
              </Link>
            )}
            actions={
              <div className="mer-header-actions">
                <div className="mer-store-identity">
                  <span className="mer-store-avatar">
                    <Store size={25} />
                  </span>
                  <div>
                    <strong>
                      {branches.find((branch) => branch.id === branchId)?.name || 'Your business'}
                    </strong>
                    <span>Merchant workspace</span>
                  </div>
                </div>
                <MerchantStoreSelector
                  liveOrderCount={liveCount}
                  socketsLive={socketsLive}
                  showBranchName={false}
                />
                {shouldShowTradingPauseControl(roles) ? <MerchantStoreStatusToggle /> : null}
                <MerchantNotificationsBell />
                <AccountMenu
                  label={accountLabel}
                  subtitle="Merchant"
                  onAfterLogout={clearStaffLabel}
                />
              </div>
            }
          />
        }
        nav={
          <nav
            aria-label="Merchant primary"
            className={cn(
              'mer-sidebar flex shrink-0 flex-col',
              'md:sticky md:top-0 md:w-60',
              'md:h-[calc(100vh-3.25rem)] md:max-h-[calc(100vh-3.25rem)] md:overflow-hidden',
            )}
            data-testid="merchant-sidebar"
          >
            {isBranchSetup ? (
              <div className="mer-setup-sidebar-banner">
                <Link to={onboardingChecklistPath()} className="mer-setup-sidebar-back">
                  ← Back to checklist
                </Link>
                <p className="mer-setup-sidebar-label">Branch setup</p>
              </div>
            ) : null}
            <p className="mer-nav-caption">Workspace</p>
            {homeHref !== '/onboarding-checklist' && roles.includes('ROLE-MER-001') ? (
              <NavLink
                to="/merchant-dashboard"
                className={({ isActive }) => navClassName(isActive)}
              >
                <LayoutDashboard size={18} aria-hidden="true" />
                <span>Dashboard</span>
              </NavLink>
            ) : null}
            <ul
              className={cn(
                'm-0 flex min-h-0 list-none flex-col gap-xs overflow-y-auto p-0 flex-1',
                isBranchSetup && 'mer-nav-dimmed',
              )}
            >
              {visibleNav.map((item, index) => (
                <li key={item.href}>
                  {index === 0 || navGroup(item.icon) !== navGroup(visibleNav[index - 1].icon) ? (
                    <p className="mer-sidebar-group-label">{navGroup(item.icon)}</p>
                  ) : null}
                  <Link
                    to={item.href}
                    className={navClassName(
                      location.pathname !== '/merchant-dashboard' &&
                        resolveMerchantNavSection(location.pathname) === item.icon,
                    )}
                    aria-current={
                      location.pathname !== '/merchant-dashboard' &&
                      resolveMerchantNavSection(location.pathname) === item.icon
                        ? 'page'
                        : undefined
                    }
                    data-nav-screen={item.screenId}
                  >
                    <MerchantNavIcon icon={item.icon} size={18} />
                    <span className="flex-1">{item.label}</span>
                    {item.badgeIncoming && incomingCount > 0 ? (
                      <span
                        className="mer-notif-badge"
                        style={{ position: 'static' }}
                        aria-label={`${incomingCount} incoming orders`}
                      >
                        {incomingCount > 99 ? '99+' : incomingCount}
                      </span>
                    ) : null}
                  </Link>
                  {location.pathname !== '/merchant-dashboard' &&
                  resolveMerchantNavSection(location.pathname) === item.icon ? (
                    <MerchantSectionLinks section={item.icon} roles={roles} />
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="mer-sidebar-footer">
              <span className="mer-sidebar-footer-mark">B</span>
              <div>
                <strong>Your business, connected.</strong>
                <span>BeatQ Eats Merchant</span>
              </div>
            </div>
          </nav>
        }
      >
        <MerchantSectionGate>
          <Outlet />
        </MerchantSectionGate>
      </AppShell>
    </div>
  );
}
